import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Button,
    Box,
    VStack,
    Text,
    Flex,
    Input,
    Spinner,
    useToast,
    Divider,
    IconButton,
    HStack,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
} from "@chakra-ui/react";
import { useState, useContext, useEffect } from "react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import moment from "moment";

const EmergingRegionDrawer = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { toastNotify } = useContext(AppContext);
    const toast = useToast();
    const [regions, setRegions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [newRegionName, setNewRegionName] = useState("");
    const [editingRegion, setEditingRegion] = useState<any>(null);
    const [editName, setEditName] = useState("");

    const fetchRegions = async () => {
        try {
            setLoading(true);
            const response: any = await appRequest("EmergingRegion", "getall", {});
            if (response?.status === "success") {
                setRegions(Array.isArray(response.data) ? response.data : []);
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to fetch regions",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Fetch regions error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchRegions();
        }
    }, [isOpen]);

    const handleAddRegion = async () => {
        if (!newRegionName.trim()) {
            toast({
                title: "Region name is required",
                status: "warning",
                duration: 2000,
            });
            return;
        }

        try {
            setAddLoading(true);
            const response: any = await appRequest("EmergingRegion", "create", {
                processName: "Emerging",
                regionName: newRegionName.trim(),
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Region added successfully",
                    type: "success",
                });
                setNewRegionName("");
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to add region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Add region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleUpdateRegion = async () => {
        if (!editName.trim() || !editingRegion?._id) return;

        try {
            setAddLoading(true);
            const response: any = await appRequest("EmergingRegion", "update", {
                id: editingRegion._id,
                regionName: editName.trim(),
            });

            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Region updated successfully",
                    type: "success",
                });
                setEditingRegion(null);
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to update region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Update region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteRegion = async (id: string) => {
        if (!id) return;
        try {
            const response: any = await appRequest("EmergingRegion", "delete", { id });
            if (response?.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "Region deleted successfully",
                    type: "success",
                });
                fetchRegions();
            } else {
                toastNotify({
                    title: "Error",
                    message: response?.message || "Failed to delete region",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Delete region error:", error);
            toastNotify({
                title: "Error",
                message: error.response?.data?.message || error.message || "Something went wrong",
                type: "error",
            });
        }
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
            <DrawerOverlay backdropFilter="blur(5px)" bg="rgba(0,0,0,0.1)" />
            <DrawerContent maxW="80vw" borderRadius="2xl" boxShadow="2xl">
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" pb={2}>
                    <Text fontSize="xl" fontWeight="800" color="teal.500">
                        Emerging Regions
                    </Text>
                    <Text fontSize="xs" color="gray.500" fontWeight="500">
                        Manage regions for the Emerging process
                    </Text>
                </DrawerHeader>

                <DrawerBody>
                    <VStack spacing={6} align="stretch" py={4}>
                        <Box bg="teal.50" p={4} borderRadius="xl" border="1px solid" borderColor="teal.100">
                            <Text fontSize="sm" fontWeight="700" mb={3} color="teal.700">
                                {editingRegion ? "Update Region" : "Add New Region"}
                            </Text>
                            <HStack>
                                <Input
                                    placeholder="Region name (e.g. UK, USA)"
                                    value={editingRegion ? editName : newRegionName}
                                    onChange={(e) => editingRegion ? setEditName(e.target.value) : setNewRegionName(e.target.value)}
                                    bg="white"
                                    borderRadius="lg"
                                    fontSize="sm"
                                    _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                />
                                <Button
                                    colorScheme="teal"
                                    onClick={editingRegion ? handleUpdateRegion : handleAddRegion}
                                    isLoading={addLoading}
                                    leftIcon={editingRegion ? <EditIcon /> : <AddIcon />}
                                    borderRadius="lg"
                                    px={6}
                                    fontSize="sm"
                                >
                                    {editingRegion ? "Update" : "Add"}
                                </Button>
                                {editingRegion && (
                                    <Button size="sm" variant="ghost" onClick={() => setEditingRegion(null)}>
                                        Cancel
                                    </Button>
                                )}
                            </HStack>
                        </Box>

                        <Divider />

                        <Box>
                            <Text fontSize="sm" fontWeight="700" mb={4} color="gray.700">
                                Existing Regions
                            </Text>
                            {loading ? (
                                <Flex justify="center" align="center" py={10}>
                                    <Spinner color="teal.500" />
                                </Flex>
                            ) : regions.length > 0 ? (
                                <TableContainer border="1px solid" borderColor="gray.100" borderRadius="xl" bg="gray.50">
                                    <Table variant="simple" size="sm">
                                        <Thead bg="gray.100">
                                            <Tr>
                                                <Th py={4}>Region Name</Th>
                                                <Th py={4}>Process</Th>
                                                <Th py={4}>Created By</Th>
                                                <Th py={4}>Created At</Th>
                                                <Th py={4} textAlign="right">Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {regions.map((region) => (
                                                <Tr
                                                    key={region._id}
                                                    _hover={{ bg: "white" }}
                                                    transition="all 0.2s"
                                                >
                                                    <Td>
                                                        <HStack>
                                                            <Badge colorScheme="teal" variant="subtle" borderRadius="full" px={2}>
                                                                {region.regionName?.charAt(0) || "R"}
                                                            </Badge>
                                                            <Text fontWeight="600">{region.regionName}</Text>
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <Badge variant="outline" colorScheme="blue" borderRadius="md">
                                                            {region.processName}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {region.createdBy?.name || region.createdBy?.username || "System"}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color="gray.600">
                                                            {region.created_At ? moment(region.created_At).format("DD-MM-YYYY HH:mm") : "N/A"}
                                                        </Text>
                                                    </Td>
                                                    <Td textAlign="right">
                                                        <HStack spacing={1} justify="flex-end">
                                                            <IconButton
                                                                aria-label="Edit region"
                                                                icon={<EditIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="blue"
                                                                onClick={() => {
                                                                    setEditingRegion(region);
                                                                    setEditName(region.regionName);
                                                                }}
                                                            />
                                                            <IconButton
                                                                aria-label="Delete region"
                                                                icon={<DeleteIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="red"
                                                                onClick={() => handleDeleteRegion(region._id)}
                                                            />
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Flex direction="column" align="center" justify="center" py={10} color="gray.400">
                                    <Text fontSize="sm">No regions found</Text>
                                </Flex>
                            )}
                        </Box>
                    </VStack>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={onClose} borderRadius="lg">
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default EmergingRegionDrawer;
